/**
 * Renders one assistant turn: the answer bubble and an optional inline chart (the
 * 3.1 horizontal-bar primitive, which carries its own `role="img"` + hidden data
 * table). Every value shown comes from the API answer — there is no client-side
 * data.
 *
 * The turn deliberately shows no "Open in dashboard" / "Show query" affordances:
 * the chat panel reads as a plain conversation, and an aggregation pipeline is
 * not something to put in front of the person asking "which type do we scan
 * most?". The API still returns `query` and the gateway still audits every
 * aggregation, so the query behind a number remains recoverable — it is simply
 * not chrome in this surface.
 */
import type { Brand } from '../../api/types';
import { HorizontalBar } from '../../charts';

import { BrandTile } from './BrandTile';
import { chartSummary, chartTitle, chartToBars } from './format';
import type { AssistantTurn } from './types';

export interface BotAnswerProps {
  turn: AssistantTurn;
  brand: Brand;
  logoUrl: string | null;
}

export function BotAnswer({ turn, brand, logoUrl }: BotAnswerProps): React.JSX.Element {
  const { chart } = turn;

  return (
    <div className="ds-chat-turn-bot">
      <BrandTile brand={brand} logoUrl={logoUrl} size="message" />
      <div className="ds-chat-bot">
        <div className="ds-chat-bot__text">{turn.answer}</div>

        {chart !== null && chart.data.length > 0 && (
          <div className="ds-chat-chart">
            <div className="ds-chat-chart__title">{chartTitle(chart)}</div>
            <HorizontalBar data={chartToBars(chart)} summary={chartSummary(chart)} />
          </div>
        )}
      </div>
    </div>
  );
}
